import React, { useState, useEffect } from 'react';
import { IconSearch, IconPhone, IconCheck, IconX } from '../components/Icons';
import { db } from '../firebase';
import { doc, deleteDoc, updateDoc, collection, onSnapshot, query, orderBy, where } from 'firebase/firestore';

export default function Users() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch real users from Firestore (only customers)
    const q = query(collection(db, "users"), where("role", "==", "customer"));
    const unsub = onSnapshot(q, (snap) => {
      let usersData = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Format joinDate for display if it exists
        joinDateFormatted: doc.data().joinDate?.toDate().toLocaleDateString('en-IN') || 'Recent'
      }));

      // Sort in memory to avoid Firestore index errors
      usersData.sort((a, b) => {
        const timeA = a.joinDate?.seconds || 0;
        const timeB = b.joinDate?.seconds || 0;
        return timeB - timeA;
      });

      setUsers(usersData);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Delete demo users (status 'Pending')
  const deleteDemoData = async () => {
    const demoDocs = users.filter(u => u.status === 'Pending');
    for (const u of demoDocs) {
      await deleteDoc(doc(db, 'users', u.id));
    }
    alert('Demo users deleted');
  };

  const updateStatus = async (userObj, status) => {
    try {
      const updateData = { status, isApproved: status === 'Active' };
      if (status === 'Active' && !userObj.adminPassword && (userObj.role === 'shopkeeper' || userObj.role === 'deliveryboy')) {
        updateData.adminPassword = Math.floor(100000 + Math.random() * 900000).toString();
      }
      await updateDoc(doc(db, 'users', userObj.id), updateData);
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const filtered = users.filter(u => {
    const name = u.name || 'User';
    const phone = u.phone || '';
    const village = u.village || '';
    
    const matchSearch = name.toLowerCase().includes(search.toLowerCase()) ||
      phone.includes(search) || village.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'All' || u.status === filter;
    return matchSearch && matchFilter;
  });

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <div className="search-bar" style={{ flex: 1 }}>
          <IconSearch size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <input
            placeholder="कस्टमर शोधा | Search customers..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {['All', 'Active', 'Pending', 'Blocked'].map(f => (
          <button key={f}
            className={`btn ${filter === f ? 'btn-primary' : 'btn-secondary'} btn-sm`}
            onClick={() => setFilter(f)}>
            {f}
          </button>
        ))}
        {/* Delete demo data button */}
        <button className="btn btn-danger btn-sm" onClick={deleteDemoData}>Delete Demo Data</button>
      </div>

      {/* Summary */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {[
          { l: 'Total Customers', v: users.length, c: 'var(--text-primary)' },
          { l: 'Active Now', v: users.filter(u => u.status === 'Active').length, c: 'var(--success)' },
          { l: 'Blocked', v: users.filter(u => u.status === 'Blocked').length, c: 'var(--danger)' },
        ].map((s, i) => (
          <div key={i} style={{
            flex: 1, padding: '12px 16px',
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 10, textAlign: 'center'
          }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: s.c }}>{s.v}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.l}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="table-wrapper">
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading users...</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Village</th>
                  <th>Orders</th>
                  <th>Total Spent</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u, i) => (
                  <tr key={u.id}>
                    <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{u.id.substring(0, 6)}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%',
                          background: `hsl(${((u.name || 'U').charCodeAt(0) * 30) % 360}, 60%, 45%)`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: 'white', fontSize: 13, fontWeight: 700, flexShrink: 0
                        }}>
                          {(u.name || 'U')[0]}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{u.name || 'User'}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Joined {u.joinDateFormatted}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <IconPhone size={12} style={{ color: 'var(--text-muted)' }} />
                        {u.phone}
                      </div>
                    </td>
                    <td style={{ fontSize: 13 }}>
                      📍 {u.village || 'Sangola'}
                      {u.adminPassword && (
                        <div style={{ color: 'var(--info)', fontSize: 11, fontWeight: '700', marginTop: 4 }}>
                          Pass: {u.adminPassword}
                        </div>
                      )}
                    </td>
                    <td style={{ fontWeight: 700 }}>{u.orders || 0}</td>
                    <td style={{ fontWeight: 700, color: 'var(--success)' }}>₹{u.spent || 0}</td>
                    <td>
                      <span className={`badge ${u.status === 'Active' ? 'success' : 'danger'}`}>
                        {u.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {u.status === 'Pending' ? (
                          <button onClick={() => updateStatus(u, 'Active')} className="btn btn-success btn-sm" title="Approve User">
                            <IconCheck size={12} /> Approve
                          </button>
                        ) : u.status === 'Active' ? (
                          <button onClick={() => updateStatus(u, 'Blocked')} className="btn btn-danger btn-sm" title="Block User">
                            <IconX size={12} /> Block
                          </button>
                        ) : (
                          <button onClick={() => updateStatus(u, 'Active')} className="btn btn-success btn-sm" title="Unblock User">
                            <IconCheck size={12} /> Unblock
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
